import _get from 'lodash/get';
import type {
  SubmissionsDetailsResponseDto,
  SubmissionDetailsWithConfidenceDto,
} from '../../../api/submissions/submissions.api.types';
import type { SubmissionCoverageDto } from '../../../api/submissions/submission-coverages.types';

interface ConfidenceScores {
  firmographics: Record<string, number>;
  summary: Record<string, number>;
  submissionCoverage: Record<string, number>;
  questions: Record<string, number>;
}

export function getConfidenceScoresFromResponse(
  response: SubmissionsDetailsResponseDto | null | undefined
): ConfidenceScores {
  const scores = {
    firmographics: {},
    summary: {},
    submissionCoverage: {},
    questions: {},
  };

  if (!response || typeof response !== 'object') {
    return scores;
  }

  // prepare firmographics scores
  const firmographicFields = response.firmographics || {};
  const firmographicScores = prepareConfidenceScores(firmographicFields);

  const submissionCoverageFields = response.submissionCoverage || {};
  const submissionCoverageScores = prepareConfidenceScores(
    submissionCoverageFields
  );

  const questions =
    (
      response as SubmissionsDetailsResponseDto & {
        questions?: Record<string, FieldWithConfidence>;
      }
    ).questions || {};
  const questionScores = prepareConfidenceScores(questions);

  return {
    firmographics: firmographicScores,
    summary: {}, // TODO: Add summary scores if needed
    submissionCoverage: submissionCoverageScores,
    questions: questionScores,
  };
}

interface FieldWithConfidence {
  confidence?: number;
  [key: string]: unknown;
}

function prepareConfidenceScores(
  fields: Record<string, FieldWithConfidence>
): Record<string, number> {
  return Object.keys(fields).reduce((prev, key) => {
    const item = fields[key];
    const hasConfidenceField =
      item != null && typeof item === 'object' && 'confidence' in item;

    if (!hasConfidenceField) {
      return prev;
    }

    return {
      ...prev,
      [key]: (item.confidence || 0) * 0.01,
    };
  }, {});
}

interface FieldsetGroupConfigItem {
  key: string;
  accessorKey?: string;
}

interface FieldsetGroupConfig {
  [groupKey: string]: FieldsetGroupConfigItem[];
}

const FIRMO_FIELD_FORM_GROUPS: FieldsetGroupConfig = {
  firmoAccountInfo: [
    { key: 'name', accessorKey: 'firmographics.name' },
    { key: 'dbaOrTradestyle', accessorKey: 'firmographics.dbaOrTradestyle' },
    { key: 'physicalAddress', accessorKey: 'firmographics.physicalAddress' },
    { key: 'mailingAddress', accessorKey: 'firmographics.mailingAddress' },
    { key: 'phoneNumber', accessorKey: 'firmographics.phoneNumber' },
    { key: 'ein', accessorKey: 'firmographics.ein' },
    { key: 'domainName', accessorKey: 'firmographics.domainName' },
    { key: 'otherDomains', accessorKey: 'firmographics.otherDomains' },
    { key: 'url', accessorKey: 'firmographics.url' },
  ],

  firmoDetails: [
    { key: 'domesticRevenue', accessorKey: 'firmographics.domesticRevenue' },
    { key: 'foreignRevenue', accessorKey: 'firmographics.foreignRevenue' },
    { key: 'noOfEmployeesAll', accessorKey: 'firmographics.noOfEmployeesAll' },
    { key: 'yearEstablished', accessorKey: 'firmographics.yearEstablished' },
    {
      key: 'numberOfPersonalRecords',
      accessorKey: 'firmographics.numberOfPersonalRecords',
    },
    { key: 'ownershipType', accessorKey: 'firmographics.ownershipType' },
    { key: 'entityType', accessorKey: 'firmographics.entityType' },
    { key: 'isFranchise', accessorKey: 'firmographics.isFranchise' },
  ],

  firmoIndustry: [
    { key: 'naicsCode', accessorKey: 'firmographics.naicsCode' },
    {
      key: 'secondNaicsCode',
      accessorKey: 'firmographics.secondNaicsCode',
    },
    { key: 'sicCode', accessorKey: 'firmographics.sicCode' },
  ],

  firmoANI: [
    {
      key: 'additionalNamedInsured',
      accessorKey: 'firmographics.additionalNamedInsured',
    },
  ],

  firmoOrganizationContact: [
    {
      key: 'policyContactFirstName',
      accessorKey: 'firmographics.policyContactFirstName',
    },
    {
      key: 'policyContactLastName',
      accessorKey: 'firmographics.policyContactLastName',
    },
    {
      key: 'policyContactEmailAddress',
      accessorKey: 'firmographics.policyContactEmailAddress',
    },
    {
      key: 'policyContactPhoneNumber',
      accessorKey: 'firmographics.policyContactPhoneNumber',
    },
  ],

  firmoSecurityContact: [
    {
      key: 'securityContactFirstName',
      accessorKey: 'firmographics.securityContactFirstName',
    },
    {
      key: 'securityContactLastName',
      accessorKey: 'firmographics.securityContactLastName',
    },
    {
      key: 'securityContactEmailAddress',
      accessorKey: 'firmographics.securityContactEmailAddress',
    },
    {
      key: 'securityContactPhoneNumber',
      accessorKey: 'firmographics.securityContactPhoneNumber',
    },
    {
      key: 'securityContactTitle',
      accessorKey: 'firmographics.securityContactTitle',
    },
    {
      key: 'securityContactCompanyName',
      accessorKey: 'firmographics.securityContactCompanyName',
    },
  ],
};

const SUBMISSION_COVERAGE_FIELD_FORM_GROUPS: FieldsetGroupConfig = {
  submissionCoverage: [
    // {
    //   key: 'customQuotingReason',
    //   accessorKey: 'submissionCoverage.customQuotingReason',
    // },
    {
      key: 'customQuotingRequired',
      accessorKey: 'submissionCoverage.customQuotingRequired',
    },
    // {
    //   key: 'policyEffectiveEndDate',
    //   accessorKey: 'submissionCoverage.policyEffectiveEndDate',
    // },
    {
      key: 'policyEffectiveStartDate',
      accessorKey: 'submissionCoverage.policyEffectiveStartDate',
    },
  ],
};

export function groupConfidenceScoresByFieldset(
  confidenceScores: ConfidenceScores
) {
  const firmographics = getFieldsetWithConfidence(
    FIRMO_FIELD_FORM_GROUPS,
    confidenceScores
  );

  const submissionCoverage = getFieldsetWithConfidence(
    SUBMISSION_COVERAGE_FIELD_FORM_GROUPS,
    confidenceScores
  );

  return {
    ...firmographics,
    ...submissionCoverage,
  };
}

interface FieldsetWithTotal {
  [key: string]: number;
  _total: number;
}

export function mapGroupConfidenceScoresToFieldsets(
  groupConfidenceScores: NonNullable<
    SubmissionDetailsWithConfidenceDto['groupConfidenceScores']
  >,
  existingFieldsets: Record<string, FieldsetWithTotal>
): Record<string, FieldsetWithTotal> {
  const groupScoreMapping = {
    firmoAccountInfo: groupConfidenceScores.accountInfoConfidenceScore / 100, // Convert to 0-1 range
    firmoDetails: groupConfidenceScores.detailsConfidenceScore / 100,
    firmoIndustry: groupConfidenceScores.industryConfidenceScore / 100,
    firmoANI: groupConfidenceScores.aniConfidenceScore / 100,
    firmoOrganizationContact:
      groupConfidenceScores.organizationContactConfidenceScore / 100,
    firmoSecurityContact:
      groupConfidenceScores.securityContactConfidenceScore / 100,
    submissionCoverage:
      groupConfidenceScores.submissionCoveragesConfidenceScore / 100,
    dynamicFields: groupConfidenceScores.dynamicFieldsConfidenceScore / 100,
  };

  return Object.keys(existingFieldsets).reduce<
    Record<string, FieldsetWithTotal>
  >((acc, fieldsetKey) => {
    const fieldset = existingFieldsets[fieldsetKey];
    const groupScore =
      groupScoreMapping[fieldsetKey as keyof typeof groupScoreMapping];

    if (groupScore !== undefined) {
      // Update the _total with the group score while keeping individual scores
      acc[fieldsetKey] = {
        ...fieldset,
        _total: groupScore,
      };
    } else {
      // Keep the fieldset unchanged if no group score mapping exists
      acc[fieldsetKey] = fieldset;
    }

    return acc;
  }, {});
}

export function getOverAllConfidenceFromFieldsets(
  fieldsets: Record<string, FieldsetWithTotal>
): number {
  const fieldsetKeys = Object.keys(fieldsets);

  const fieldsetTotal = fieldsetKeys.reduce((prev, key: string) => {
    const fieldset = fieldsets[key];
    const total = fieldset._total || 0;

    return prev + total;
  }, 0);

  return fieldsetTotal / fieldsetKeys.length || 0;
}

function getFieldsetWithConfidence(
  config: FieldsetGroupConfig,
  confidenceScores: ConfidenceScores
) {
  return Object.keys(config).reduce((prev, groupKey: string) => {
    const fields: FieldsetGroupConfigItem[] = config[groupKey];

    const scores = fields.reduce((acc, fieldConfig) => {
      // The key used to access the confidence score from the response
      const dataAccessorKey = fieldConfig.accessorKey || fieldConfig.key;
      let score = _get(confidenceScores, dataAccessorKey);
      const isValidScore = score !== null && score !== undefined;

      if (!isValidScore) {
        score = 0;
      }

      return {
        ...acc,
        [fieldConfig.key]: score,
      };
    }, {});

    const total = getTotalFieldsetConfidence(scores);

    return { ...prev, [groupKey]: { _total: total, ...scores } };
  }, {});
}

/**
 * Dynamic question forms report per-field confidence on a 0-100 scale, while the
 * confidence provider stores 0-1 plus a `_total` average. Shared by the two
 * places a question form's scores enter the provider: the submission-wide
 * `useGetSubmissionQuestions` sweep and each rendered panel's own registration.
 */
export function normalizeFormConfidenceScores(
  confidenceScores: Record<string, number> | null | undefined
): FieldsetWithTotal {
  if (!confidenceScores) {
    return { _total: 0 };
  }

  const scoresWithTotal = {
    ...confidenceScores,
    _total: getTotalFieldsetConfidence(confidenceScores),
  };

  return Object.entries(scoresWithTotal).reduce<FieldsetWithTotal>(
    (acc, [key, value]) => {
      acc[key] = (value ?? 0) / 100;
      return acc;
    },
    { _total: 0 }
  );
}

export function getTotalFieldsetConfidence(fieldset: Record<string, number>) {
  const scorelist: number[] = Object.keys(fieldset)
    .filter((key) => key !== '_total')
    .map((key) => fieldset[key]);

  const total = scorelist.reduce((acc, score) => acc + score, 0);
  return scorelist.length ? total / scorelist.length : 0;
}

export type FlattenedSubmissionResponse =
  Partial<SubmissionDetailsWithConfidenceDto> & {
    firmographics?: Record<string, unknown>;
    scores: ConfidenceScores;
    groupConfidenceScores?: NonNullable<
      SubmissionDetailsWithConfidenceDto['groupConfidenceScores']
    >;
    overallConfidenceScore?: number;
    submissionCoverage?: SubmissionCoverageDto;
  };

export function flattenSubmissionResponse(
  data: SubmissionsDetailsResponseDto
): FlattenedSubmissionResponse | undefined {
  if (!data) {
    return undefined;
  }

  const {
    firmographics,
    summary,
    submissionCoverage,
    groupConfidenceScores,
    overallConfidenceScore,
    confidenceCapped,
    confidenceCapReasons,
  } = data;

  const nextFirmographics = Object.keys(firmographics).reduce(
    (prev, key: string) => ({
      ...prev,
      [key]: data.firmographics[key]?.value,
    }),
    {}
  );

  return {
    ...summary,
    ...(Object.keys(nextFirmographics).length > 0 && {
      firmographics: nextFirmographics,
    }),
    scores: getConfidenceScoresFromResponse(data),
    groupConfidenceScores,
    overallConfidenceScore,
    confidenceCapped,
    confidenceCapReasons,
    submissionCoverage,
  } as FlattenedSubmissionResponse;
}

/**
 * Normalizes all confidence scores to 100% (1.0) for non-email submissions
 * @param groupedScoresWithQuestions - The combined confidence scores from submission and questions
 * @returns Normalized confidence scores with all values set to 1.0
 */
export function normalizeConfidenceScoresTo100(
  groupedScoresWithQuestions: Record<string, Record<string, number>>
): Record<string, Record<string, number>> {
  return Object.keys(groupedScoresWithQuestions).reduce((acc, fieldsetKey) => {
    const fieldset = groupedScoresWithQuestions[fieldsetKey];
    const normalizedFieldset = Object.keys(fieldset).reduce(
      (fieldsetAcc, fieldKey) => {
        fieldsetAcc[fieldKey] = 1; // 1 = 100%
        return fieldsetAcc;
      },
      {} as Record<string, number>
    );
    acc[fieldsetKey] = normalizedFieldset;
    return acc;
  }, {} as Record<string, Record<string, number>>);
}
